import { useState, useRef, useCallback, useEffect } from "react";

interface UsePanReturn {
  isDragging: boolean;
  panHandlers: {
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  };
  cursor: string;
}

/**
 * Drag-to-pan hook.
 *
 * Captures the scroll container element on pointerdown, then attaches
 * native window-level pointermove/pointerup listeners for the duration of
 * the drag so events are never lost regardless of how fast the pointer moves.
 *
 * @example
 * const pan = usePan(zoom > 1);
 * <div className="overflow-auto" style={{ cursor: pan.cursor }} {...pan.panHandlers}>
 *   <div style={{ zoom }} className="h-full">…content…</div>
 * </div>
 */
export function usePan(enabled: boolean): UsePanReturn {
  const [isDragging, setIsDragging] = useState(false);

  const dragData = useRef<{
    el: HTMLElement;
    startX: number;
    startY: number;
    startScrollLeft: number;
    startScrollTop: number;
  } | null>(null);

  // React synthetic event — just records the target element + drag origin
  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!enabled || e.button !== 0) return;
      e.preventDefault();
      const el = e.currentTarget as HTMLElement;
      dragData.current = {
        el,
        startX: e.clientX,
        startY: e.clientY,
        startScrollLeft: el.scrollLeft,
        startScrollTop: el.scrollTop,
      };
      setIsDragging(true);
    },
    [enabled],
  );

  // Native window listeners while dragging — bypasses React event delegation
  useEffect(() => {
    if (!isDragging) return;

    const onMove = (e: PointerEvent) => {
      const d = dragData.current;
      if (!d) return;
      d.el.scrollLeft = d.startScrollLeft - (e.clientX - d.startX);
      d.el.scrollTop = d.startScrollTop - (e.clientY - d.startY);
    };

    const onUp = () => {
      dragData.current = null;
      setIsDragging(false);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [isDragging]);

  return {
    isDragging,
    panHandlers: { onPointerDown },
    cursor: !enabled ? "default" : isDragging ? "grabbing" : "grab",
  };
}
